import React, {useContext, useEffect, useState} from "react";
import {Modal} from "../components/Modal";
import {AccountContext} from "../context/AccountProvider";
import {sendAssistanceRequest} from "../service/accountApi";
import {toast} from "react-toastify";
import {t} from "i18next";
import {familyTreeNames} from "../data/FamilyTreeNames";
import {ChevronDownIcon} from "@radix-ui/react-icons";
import {Transition} from "@headlessui/react";
import {countryCodes} from "../data/CountryCode";

const Assistant = ({open = false, setOpen}) => {
    const {account} = useContext(AccountContext);

    const [isOpen, setIsOpen] = useState(false);

    const [formData, setFormData] = useState({
        firstName: "",
        middleName: "",
        lastName: "",
        familyTreeName: "",
        phone: {
            charCode: 'IN',
            dialingCode: '+91',
            countryName: 'India',
            number: ''
        },
        email: "",
        description: "",
    });

    const [error, setError] = useState({});

    const [isMemberOfJoglekarKul2010Granth, setIsMemberOfJoglekarKul2010Granth] = useState(false);

    const [filteredFamilyTreeNames, setFilteredFamilyTreeNames] = useState(familyTreeNames);
    const [isFamilyTreeNameDropdownOpen, setIsFamilyTreeNameDropdownOpen] = useState(false);
    const [isSelectingFamilyTreeName, setIsSelectingFamilyTreeName] = useState(false);

    const [isCountryCodeDropdownOpen, setIsCountryCodeDropdownOpen] = useState(false);
    const [isSelectingCountryCode, setIsSelectingCountryCode] = useState(false);

    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);

    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    // Prefill form with account data
    useEffect(() => {
        if (isOpen && account && Object.keys(account).length > 0) {
            setFormData({
                ...formData,
                firstName: `${account.firstName || ''}`.trim(),
                middleName: `${account.middleName || ''}`.trim(),
                lastName: `${account.lastName || ''}`.trim(),
                phone: account.phone,
                email: account.email || "",
            });
        }
    }, [isOpen]);

    // Email Validation Functions
    const validateEmail = (email) => {
        return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email);
    }

    const validateEmailId = (e) => {
        if (e.target.value !== "") {
            if (!validateEmail(e.target.value)) {
                setError(prev => ({
                    ...prev,
                    [e.target.name]: t("ASSISTANCE_FORM.EMAIL_INPUT.ERROR.INVALID_INPUT")
                }));
            } else {
                setIsSubmitButtonDisabled(false);

                if (Object.keys(error).includes(e.target.name)) {
                    setError(prev => ({
                        ...prev,
                        [e.target.name]: ""
                    }));
                }
            }
        } else {
            setIsSubmitButtonDisabled(true);

            if (Object.keys(error).includes(e.target.name)) {
                setError(prev => ({
                    ...prev,
                    [e.target.name]: ""
                }));
            }
        }
    }

    useEffect(() => {
        if (validateEmail(formData.email)) {
            setIsSubmitButtonDisabled(false);
        } else {
            setIsSubmitButtonDisabled(true);
        }
    }, [formData.email]);

    // WhatsApp Number Functions
    const handleCountryCodeSelect = (countryDetails) => {
        setIsSelectingCountryCode(false);
        setIsCountryCodeDropdownOpen(false);
        setFormData(prev => ({
            ...prev,
            phone: {
                ...prev.phone,
                charCode: countryDetails.countryCode,
                dialingCode: countryDetails.countryDialingCode,
                countryName: countryDetails.countryName
            }
        }));
    }

    const isNumberValid = (phone) => {
        let reg = /^\d+$/;
        return reg.test(phone);
    }

    const validatePhoneNumber = (e) => {
        if (e.target.value !== "") {
            if (!isNumberValid(e.target.value) || e.target.value.length < 10) {
                setError(prev => ({
                    ...prev,
                    [e.target.name]: t("ASSISTANCE_FORM.PHONE_INPUT.ERROR.INVALID_INPUT")
                }));
            } else {
                setIsSubmitButtonDisabled(false);

                if (Object.keys(error).includes(e.target.name)) {
                    setError(prev => ({
                        ...prev,
                        [e.target.name]: ""
                    }));
                }
            }
        } else {
            setIsSubmitButtonDisabled(true);

            if (Object.keys(error).includes(e.target.name)) {
                setError(prev => ({
                    ...prev,
                    [e.target.name]: ""
                }));
            }
        }
    }

    useEffect(() => {
        if (!isNumberValid(formData.phone.number) || formData.phone.number.length < 10) {
            setIsSubmitButtonDisabled(true);
        } else {
            setIsSubmitButtonDisabled(false);
        }
    }, [formData.phone]);

    useEffect(() => {
        if (formData.lastName.trim().toLowerCase() !== 'joglekar' || !isMemberOfJoglekarKul2010Granth) {
            setIsMemberOfJoglekarKul2010Granth(false);
            setFormData((prev) => ({
                ...prev,
                familyTreeName: ''
            }));
        }
    }, [formData.lastName, isMemberOfJoglekarKul2010Granth]);

    useEffect(() => {
        if (isMemberOfJoglekarKul2010Granth && formData.familyTreeName === '') {
            setIsSubmitButtonDisabled(true);
        } else {
            setIsSubmitButtonDisabled(false);
        }
    }, [formData.familyTreeName, isMemberOfJoglekarKul2010Granth]);

    useEffect(() => {
        if (
            formData.firstName && formData.middleName && formData.lastName &&
            (formData.phone.number || formData.email) && formData.description
        ) {
            if (isMemberOfJoglekarKul2010Granth && formData.familyTreeName !== '') {
                setIsSubmitButtonDisabled(false);
            } else if (!isMemberOfJoglekarKul2010Granth) {
                setIsSubmitButtonDisabled(false);
            }
        } else {
            setIsSubmitButtonDisabled(true);
        }
    }, [
        formData.firstName, formData.middleName, formData.lastName,
        formData.phone.number, formData.email, formData.description,
        formData.familyTreeName, isMemberOfJoglekarKul2010Granth
    ]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFamilyTreeNameSelect = (familyTreeName) => {
        setIsSelectingFamilyTreeName(false);
        setIsFamilyTreeNameDropdownOpen(false);
        setFormData(prev => ({
            ...prev,
            familyTreeName
        }));
    }

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(() => {
            if (setOpen) setOpen(false);
            setFormData({
                firstName: "",
                middleName: "",
                lastName: "",
                familyTreeName: "",
                phone: {
                    charCode: "IN",
                    dialingCode: "+91",
                    countryName: "India",
                    number: ""
                },
                email: "",
                description: "",
            });
        }, 200);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log("Form Submitted:", formData);
        setIsSubmitButtonDisabled(true);

        const assistanceData = {
            firstName: formData.firstName,
            middleName: formData.middleName,
            lastName: formData.lastName,
            familyTreeName: formData.familyTreeName,
            phoneNumber: `${formData.phone.dialingCode}-${formData.phone.number}`,
            email: formData.email,
            description: formData.description,
        }

        sendAssistanceRequest(assistanceData)
            .then((response) => {
                console.log("Send Assistance Request Response:", response);
                if (response?.success) {
                    toast.dismiss();
                    toast.success(t("ASSISTANCE_FORM.SEND_REQUEST.SUCCESS"), {
                        autoClose: 30000,
                        closeOnClick: false,
                        className: "portrait:w-full landscape:w-[25vw]",
                    });
                    handleClose();
                } else {
                    toast.dismiss();
                    toast.error(t("ASSISTANCE_FORM.SEND_REQUEST.ERROR"));
                }
                setIsSubmitButtonDisabled(false);
            })
            .catch((error) => {
                console.log("Error sending assistance request:", error);
                toast.dismiss();
                toast.error(t("ASSISTANCE_FORM.SEND_REQUEST.ERROR"));
                setIsSubmitButtonDisabled(false);
            });
    };

    return (
        <>
            {/* Floating Assistance Button */}
            <div className="fixed bottom-[1rem] right-[1rem] z-50">
                <button
                    onClick={() => setIsOpen(true)}
                    className={`flex items-center justify-center
                    
                        bg-blue-900 rounded-full shadow-lg
                        hover:ring-blue-500 hover:ring-offset-1 hover:ring-4
                        portrait:gap-x-[0.25rem] portrait:sm:gap-x-[0.4rem]
                        landscape:gap-x-[0.35rem] landscape:jk:gap-x-[0.5rem]
                        
                        portrait:px-[0.75rem] portrait:py-[0.55rem]
                        portrait:sm:px-[1.25rem] portrait:sm:py-[0.75rem]
                        portrait:jk:px-[1.15rem] portrait:jk:py-[0.65rem
                        
                        landscape:px-[0.75rem] landscape:py-[0.5rem]
                        landscape:jk:px-[0.95rem] landscape:jk:py-[0.75rem]
                        
                        text-white font-custom-medium leading-tight line-clamp-1
                        portrait:text-[1rem] portrait:md:text-[1.25rem] portrait:jk:text-[1.15rem]
                        landscape:text-[0.8rem] landscape:md:text-[1rem] landscape:jk:text-[1rem]
                        
                        transition-all duration-300 ease-in-out`}
                >
                    🆘 {t("ASSISTANCE_FORM.BUTTON")}
                </button>
            </div>

            {/* Assistance Modal */}
            <Modal isOpen={isOpen} zIndex="z-[200]">
                <div
                    className="form-modal-container"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className={"form-modal-header-container"}>
                        {/* Header Title | Close Button */}
                        <div className={"flex flex-row items-center justify-between"}>
                            {/* Form Title */}
                            <h2>
                                {t("ASSISTANCE_FORM.TITLE")}
                            </h2>

                            {/* Close Button */}
                            <button onClick={handleClose}>
                                <img
                                    className={"w-[75%] h-[75%]"}
                                    src={"/assets/close-icon.png"}
                                    alt={"Close"}
                                    title={"Close"}
                                />
                            </button>
                        </div>

                        {/* Divider */}
                        <div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full"}/>
                    </div>

                    {/* Form Fields */}
                    <div className="form-modal-body-container">
                        {/* Full Name */}
                        <div
                            className="!w-full custom-input-container"
                        >
                            {/* Label */}
                            <label htmlFor="firstName">
                                {t("ASSISTANCE_FORM.NAME.LABEL.BASE")}
                            </label>

                            {/* Full Name - Input Fields */}
                            <div className="custom-input-multiple-fields-container">
                                {/* First Name */}
                                {/*<div className="flex-1 flex flex-col gap-1">*/}
                                <input
                                    id="firstName"
                                    className={"custom-input w-full"}
                                    name='firstName'
                                    placeholder={t("ASSISTANCE_FORM.NAME.PLACEHOLDER.FIRST_NAME")}
                                    value={formData.firstName}
                                    onChange={handleChange}
                                />
                                {/*</div>*/}

                                {/* Middle Name */}
                                {/*<div className="flex-1 flex flex-col gap-1">*/}
                                <input
                                    id="middleName"
                                    className={"custom-input w-full"}
                                    name='middleName'
                                    placeholder={t("ASSISTANCE_FORM.NAME.PLACEHOLDER.MIDDLE_NAME")}
                                    value={formData.middleName}
                                    onChange={handleChange}
                                />
                                {/*</div>*/}

                                {/* Last Name */}
                                {/*<div className="flex-1 flex flex-col gap-1">*/}
                                <input
                                    className={"custom-input w-full"}
                                    id="lastName"
                                    name='lastName'
                                    placeholder={t("ASSISTANCE_FORM.NAME.PLACEHOLDER.LAST_NAME")}
                                    value={formData.lastName}
                                    onChange={handleChange}
                                />
                                {/*</div>*/}
                            </div>

                            {/*/!* Error Container *!/*/}
                            {/*{*/}
                            {/*    (error['firstName'] !== '' || error['middleName'] !== '' || error['lastName'] !== '') && (*/}
                            {/*        <div*/}
                            {/*            className="custom-input-multiple-fields-container portrait:hidden"*/}
                            {/*        >*/}
                            {/*            <p*/}
                            {/*                className="flex-1 text-red-600 font-custom-regular*/}
                            {/*                    portrait:text-[0.625rem] portrait:md:text-[0.9rem]*/}
                            {/*                    portrait:jk:text-[1.2rem] portrait:lg:text-[1.45rem]*/}
                            {/*                    landscape:text-[0.45rem] landscape:md:text-[0.5rem]*/}
                            {/*                    landscape:jk:text-[0.6rem] landscape:lg:text-[0.7rem]"*/}
                            {/*                id="error"*/}
                            {/*            >*/}
                            {/*                {error['firstName'] ? error['firstName'] : ''}*/}
                            {/*            </p>*/}

                            {/*            <p*/}
                            {/*                className="flex-1 text-red-600 font-custom-regular*/}
                            {/*                    portrait:text-[0.625rem] portrait:md:text-[0.9rem]*/}
                            {/*                    portrait:jk:text-[1.2rem] portrait:lg:text-[1.45rem]*/}
                            {/*                    landscape:text-[0.45rem] landscape:md:text-[0.5rem]*/}
                            {/*                    landscape:jk:text-[0.6rem] landscape:lg:text-[0.7rem]"*/}
                            {/*                id="error"*/}
                            {/*            >*/}
                            {/*                {error['middleName'] ? error['middleName'] : ''}*/}
                            {/*            </p>*/}

                            {/*            <p*/}
                            {/*                className="flex-1 text-red-600 font-custom-regular*/}
                            {/*                    portrait:text-[0.625rem] portrait:md:text-[0.9rem]*/}
                            {/*                    portrait:jk:text-[1.2rem] portrait:lg:text-[1.45rem]*/}
                            {/*                    landscape:text-[0.45rem] landscape:md:text-[0.5rem]*/}
                            {/*                    landscape:jk:text-[0.6rem] landscape:lg:text-[0.7rem]"*/}
                            {/*                id="error"*/}
                            {/*            >*/}
                            {/*                {error['lastName'] ? error['lastName'] : ''}*/}
                            {/*            </p>*/}
                            {/*        </div>*/}
                            {/*    )*/}
                            {/*}*/}
                        </div>

                        {/* Are you a Member of Joglekarkul 2010 Granth */}
                        {
                            (formData.lastName.trim().toLowerCase() === 'joglekar') && (
                                <div
                                    className={`flex flex-col items-start
                                        portrait:gap-y-[0.5rem] portrait:md:gap-y-[0.25rem]
                                        portrait:jk:gap-y-[0.45rem] portrait:lg:gap-y-[0.75rem]
                                        landscape:gap-y-[0.1rem] landscape:md:gap-y-[0.15rem]
                                        landscape:jk:gap-y-[0.2rem] landscape:lg:gap-y-[0.3rem]`}
                                >
                                    {/* Label */}
                                    <div className={`w-full flex-1 flex flex-col items-start`}>
                                        <p
                                            className={`w-full font-custom-medium leading-6 text-gray-600
                                                portrait:text-[0.85rem] portrait:md:text-[0.8rem]
                                                portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.65rem] landscape:md:text-[0.7rem]
                                                landscape:jk:text-[0.75rem] landscape:lg:text-[0.85rem]`}
                                        >
                                            {t("ASSISTANCE_FORM.MEMBER_OF_JOGLEKARKUL_2010_GRANTH.LABEL")}
                                        </p>
                                    </div>

                                    {/* Yes | No Radio Buttons */}
                                    <div
                                        className={`custom-input-multiple-fields-container 
                                            w-full flex-1`}
                                    >
                                        {/* Yes */}
                                        <label className={`custom-input-checkbox`}>
                                            <div
                                                className={`${isMemberOfJoglekarKul2010Granth ?
                                                    'bg-[#FF6600] border-orange-300'
                                                    : 'bg-white border-[#CBCBCB]'}`}
                                            >
                                                <img
                                                    src={"/assets/tick-icon-light.svg"}
                                                    alt={"Select"}
                                                    title={"Select"}
                                                />
                                                <input
                                                    type="checkbox"
                                                    className="absolute hidden"
                                                    checked={isMemberOfJoglekarKul2010Granth}
                                                    onChange={() => setIsMemberOfJoglekarKul2010Granth(true)}
                                                />
                                            </div>
                                            <span
                                                className={`flex-1 flex items-center
                                                    portrait:gap-[0.5rem] portrait:md:gap-[0.25rem] 
                                                    portrait:jk:gap-[0.45rem] portrait:lg:gap-[0.75rem]
                                                    landscape:gap-[0.1rem] landscape:md:gap-[0.15rem]
                                                    landscape:jk:gap-[0.2rem] landscape:lg:gap-[0.3rem]`}
                                            >
                                                <p
                                                    className={`portrait:text-[1rem] portrait:md:text-[1rem]
                                                        portrait:jk:text-[1.2rem] portrait:lg:text-[1.5rem]
                                                        landscape:text-[0.8rem] landscape:jk:text-[0.9rem] 
                                                        landscape:lg:text-[1rem] leading-tight 
                                                        font-custom-regular text-gray-900`}
                                                >
                                                    {t("ASSISTANCE_FORM.MEMBER_OF_JOGLEKARKUL_2010_GRANTH.OPTIONS.YES")}
                                                </p>
                                            </span>
                                        </label>

                                        {/* No */}
                                        <label className="custom-input-checkbox">
                                            <div
                                                className={`${!isMemberOfJoglekarKul2010Granth
                                                    ? 'bg-[#FF6600] border-orange-300'
                                                    : 'bg-white border-[#CBCBCB]'}`}
                                            >
                                                <img
                                                    src={"/assets/tick-icon-light.svg"}
                                                    alt={"Select"}
                                                    title={"Select"}
                                                />
                                                <input
                                                    type="checkbox"
                                                    checked={!isMemberOfJoglekarKul2010Granth}
                                                    className="absolute hidden"
                                                    onChange={() => setIsMemberOfJoglekarKul2010Granth(false)}
                                                />
                                            </div>
                                            <p
                                                className={`portrait:text-[1rem] portrait:md:text-[1rem]
                                                    portrait:jk:text-[1.2rem] portrait:lg:text-[1.5rem]
                                                    landscape:text-[0.8rem] landscape:jk:text-[0.9rem] 
                                                    landscape:lg:text-[1rem] leading-tight 
                                                    font-custom-regular text-gray-900`}
                                            >
                                                {t("ASSISTANCE_FORM.MEMBER_OF_JOGLEKARKUL_2010_GRANTH.OPTIONS.NO")}
                                            </p>
                                        </label>
                                    </div>
                                </div>
                            )
                        }

                        {/* Family Tree Name */}
                        {
                            isMemberOfJoglekarKul2010Granth && (
                                <div className="custom-input-container">
                                    <label>
                                        {t("ASSISTANCE_FORM.FAMILY_TREE_NAME.LABEL")}
                                    </label>

                                    <div className="h-full w-full flex flex-col">
                                        {/* Family Tree Name Input */}
                                        <div
                                            className={`custom-input-dropdown-button
                                                ${isFamilyTreeNameDropdownOpen && 'ring-4 ring-offset-1 ring-[#5F0F40]'}`}
                                        >
                                            <input
                                                id="familyTreeName"
                                                name="familyTreeName"
                                                value={formData.familyTreeName}
                                                onBlur={(e) => {
                                                    console.log("Family Tree Name Input Blur: ", isSelectingFamilyTreeName);
                                                    if (!isSelectingFamilyTreeName) {
                                                        setIsFamilyTreeNameDropdownOpen(false);
                                                    }
                                                }}
                                                onClick={() => setIsFamilyTreeNameDropdownOpen(true)}
                                                onKeyUp={(e) => {
                                                    if (e.key === "Escape") {
                                                        setIsFamilyTreeNameDropdownOpen(false);
                                                    }
                                                }}
                                                onFocus={(e) => {
                                                    if (e.target.value === "") {
                                                        setFilteredFamilyTreeNames(familyTreeNames);
                                                    } else {
                                                        console.log("Family Tree Name Input Value: ", e.target.value);
                                                        setFilteredFamilyTreeNames(
                                                            familyTreeNames
                                                                .filter((familyTreeName) => {
                                                                    return familyTreeName.toLowerCase().includes(e.target.value.toLowerCase());
                                                                })
                                                        );
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    if (e.target.value === "") {
                                                        setFilteredFamilyTreeNames(familyTreeNames);
                                                    } else {
                                                        console.log("Family Tree Name Input Value: ", e.target.value);
                                                        setFilteredFamilyTreeNames(
                                                            familyTreeNames
                                                                .filter((familyTreeName) => {
                                                                    return familyTreeName.toLowerCase().includes(e.target.value.toLowerCase());
                                                                })
                                                        );
                                                    }

                                                    setFormData(prev => ({
                                                        ...prev,
                                                        [e.target.name]: e.target.value
                                                    }));
                                                }}
                                            />

                                            <ChevronDownIcon
                                                className={`${isFamilyTreeNameDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
                                                aria-hidden="true"
                                                stroke="#292D32"
                                                strokeWidth={"0.1vh"}
                                                onClick={() => setIsFamilyTreeNameDropdownOpen(!isFamilyTreeNameDropdownOpen)}
                                            />
                                        </div>

                                        {/* Family Tree Name Dropdown */}
                                        <div className={"relative"}>
                                            <Transition show={isFamilyTreeNameDropdownOpen}>
                                                <div
                                                    className={`bg-white absolute left-0 right-0 z-[100]
                                                        origin-top-right rounded-md shadow-2xl mt-[0.5vh]
                                                        
                                                        landscape:mx-[0.75rem] landscape:jk:mx-[0.9rem] landscape:lg:mx-[1.15rem]
                                                        
                                                        border border-gray-700
                                                        data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                        data-[enter]:duration-100 data-[enter]:ease-out 
                                                        data-[leave]:duration-75 data-[leave]:ease-in`}
                                                >
                                                    <div
                                                        className={`w-full flex-1 overflow-hidden 
                                                                relative rounded-md`}
                                                    >
                                                        <div
                                                            className="flex-1 flex flex-col max-h-[30vh] w-full overflow-y-scroll"
                                                        >
                                                            {
                                                                filteredFamilyTreeNames.length > 0
                                                                    ? filteredFamilyTreeNames.map((familyTreeName, index) => (
                                                                        <button
                                                                            key={index}
                                                                            className={`custom-input-dropdown-list-button`}
                                                                            onMouseDown={() => setIsSelectingFamilyTreeName(true)}
                                                                            onClick={() => handleFamilyTreeNameSelect(familyTreeName)}
                                                                        >
                                                                            <p className={`text-nowrap overflow-x-scroll no-scrollbar`}>
                                                                                {familyTreeName}
                                                                            </p>
                                                                        </button>
                                                                    )) : (
                                                                        <button
                                                                            className={`custom-input-dropdown-list-button`}
                                                                            onClick={() => setIsFamilyTreeNameDropdownOpen(false)}
                                                                        >
                                                                            <p className={`text-nowrap overflow-x-scroll no-scrollbar`}>
                                                                                {formData.familyTreeName}
                                                                            </p>
                                                                        </button>
                                                                    )
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </Transition>
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {/* WhatsApp Number */}
                        <div className="custom-phone-number-input">
                            <label htmlFor="phone">
                                {t("ASSISTANCE_FORM.PHONE_INPUT.LABEL")}
                            </label>
                            <div
                                className="flex-1 flex flex-col
                                    portrait:gap-2 portrait:jk:gap-4 landscape:gap-2"
                            >
                                {/* Country Code | WhatsApp Number Input */}
                                <div className="custom-phone-number-input-container">
                                    {/* Country Code Button */}
                                    <button
                                        onBlur={() => {
                                            if (!isSelectingCountryCode) {
                                                setIsCountryCodeDropdownOpen(false);
                                            }
                                        }}
                                        onClick={() => setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen)}
                                    >
                                        <img
                                            src={`assets/country_flags/${formData.phone.charCode}.png`}
                                            className={"w-[1rem] aspect-auto"}
                                            alt={formData.phone.countryName}
                                        />
                                        <p
                                            className={""}
                                            aria-selected={"false"}
                                        >
                                            {formData.phone.dialingCode}
                                        </p>
                                        <ChevronDownIcon
                                            // onClick={() => setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen)}
                                            className={`${isCountryCodeDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
                                            aria-hidden="true"
                                            stroke="#292D32"
                                            strokeWidth={"0.1vh"}
                                        />
                                    </button>

                                    {/* WhatsApp Number Input */}
                                    <input
                                        type="phone"
                                        name="phone"
                                        className=""
                                        placeholder="7734567890"
                                        value={formData.phone.number || ""}
                                        onChange={(e) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                [e.target.name]: {
                                                    ...prev.phone,
                                                    number: e.target.value
                                                }
                                            }));

                                            setError(prev => ({
                                                ...prev,
                                                [e.target.name]: ""
                                            }));
                                        }}
                                        onBlur={validatePhoneNumber}
                                    />
                                </div>

                                {/* Country Code Dropdown */}
                                <div className={`relative`}>
                                    <Transition show={isCountryCodeDropdownOpen}>
                                        <div
                                            className={`bg-white absolute left-0 right-0 z-[100]
                                                origin-top-right rounded-md shadow-2xl mt-[0.5vh]
                                                
                                                landscape:mx-[0.75rem] landscape:jk:mx-[0.9rem] landscape:lg:mx-[1.15rem]
                                                
                                                border border-gray-700
                                                data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                data-[enter]:duration-100 data-[enter]:ease-out 
                                                data-[leave]:duration-75 data-[leave]:ease-in`}
                                        >
                                            <div
                                                className={"w-full flex-1 overflow-hidden relative rounded-md"}>
                                                <div
                                                    className="flex-1 flex flex-col max-h-[30vh] w-full overflow-y-scroll"
                                                >
                                                    {
                                                        countryCodes.map((country, index) => (
                                                            <button
                                                                key={index}
                                                                className={`custom-input-dropdown-list-button`}
                                                                onMouseDown={() => setIsSelectingCountryCode(true)}
                                                                onClick={() => handleCountryCodeSelect(country)}
                                                            >
                                                                <img
                                                                    src={country.countryFlagSrc}
                                                                    alt={country.countryName}
                                                                />
                                                                <p
                                                                    className={`text-nowrap`}
                                                                >
                                                                    {`${country.countryName}  (${country.countryDialingCode})`}
                                                                </p>
                                                            </button>
                                                        ))
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </Transition>
                                </div>
                            </div>

                            {error['phone'] && (
                                <p
                                    id="phone-error"
                                    className="custom-input-error-message"
                                >
                                    {error['phone']}
                                </p>
                            )}
                        </div>

                        {/* Email ID */}
                        <div className="custom-input-container">
                            <label htmlFor="email">
                                {t("ASSISTANCE_FORM.EMAIL_INPUT.LABEL")}
                            </label>

                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                id="email"
                                className={`custom-input`}
                                onBlur={validateEmailId}
                            />
                        </div>

                        {/* Assistance Description */}
                        <div className="custom-input-container">
                            <label htmlFor="email">
                                {t("ASSISTANCE_FORM.DESCRIPTION.LABEL")}
                            </label>

                            <textarea
                                name="description"
                                placeholder={t("ASSISTANCE_FORM.DESCRIPTION.PLACEHOLDER")}
                                value={formData.description}
                                onChange={handleChange}
                                className={`custom-input min-h-[20vh] h-[20vh]`}
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className={"form-modal-footer-container"}>
                        {/* Divider */}
                        <div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full"}/>

                        <div className="flex items-center justify-end">
                            <button
                                disabled={isSubmitButtonDisabled}
                                type="submit"
                                className="form-modal-footer-container-submit-button"
                                onClick={handleSubmit}
                            >
                                <p>{t("ASSISTANCE_FORM.SUBMIT")}</p>
                                <svg
                                    viewBox="0 0 6 6"
                                    fill="none"
                                    id={"arrow-icon"}
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M3 1L5 3L3 5"
                                        id={"arrow-icon-tip"}
                                        stroke="black"
                                        strokeLinecap="square"
                                    />
                                    <path
                                        d="M1 3L4 3"
                                        id={"arrow-icon-line"}
                                        stroke="black"
                                        strokeLinecap="square"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default Assistant;
